<template>
  <ModalCreateEditNews
    v-show="modalInfoNews"
    :news="selectedNews"
    @close="closeInformationNewsModal"
    @create="createNewsTransaction"
    @edit="editNewsTransaction"
  />
  <ModalConfirmation
    :message="confirmMessage"
    @accept="closeConfirmModalAsync(deleteNewsTransaction)"
    @deny="closeConfirmModalAsync"
    v-show="showConfirmModal"
  />
  <div class="wrapper-news">
    <div class="above-info">
      <p>Noticias</p>
      <StandardButton :buttonLabel="'Crear noticia'" @click="createNewNews" />
    </div>

    <ul class="wrapper-news">
      <li class="news" v-for="(news, index) in news" :key="index">
        <div class="news-title">
          <p>{{ news.title }}</p>
        </div>
        <EditButton @click="editNewsSelected(news)" :type="'Edit'" />
        <DeleteButton @click="selectDeletedNews(news)" :type="'Delete'" />
        <img :src="news.slider" class="news-image" />
        <div class="wrapper-text">
          <p>{{ news.body }}</p>
        </div>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import ModalCreateEditNews from "./components/ModalCreateEditNews.vue";
import ModalConfirmation from "@/components/commons/modals/ModalConfirmation.vue";
import { newsUse } from "../news/use/newsUse";
import StandardButton from "@/components/commons/controls/StandardButton.vue";
import EditButton from "@/components/commons/controls/EditButton.vue";
import DeleteButton from "@/components/commons/controls/DeleteButton.vue";
import { spinnerProcessKey, successProcessKey } from "@/main";
import { confirmCancelMessageManager } from "@/ux/utils/managers/confirmCancelMessageManager";
import { News } from "@/business/news/domain/news";

export default defineComponent({
  name: "NewsView",
  components: {
    ModalCreateEditNews,
    ModalConfirmation,
    StandardButton,
    EditButton,
    DeleteButton,
  },

  setup() {
    const successProcess = inject(successProcessKey)!;
    const { showSuccessModal } = successProcess;

    const spinnerProcess = inject(spinnerProcessKey)!;
    const { offSpinnerState, onSpinnerState } = spinnerProcess;

    const {
      showConfirmModal,
      openConfirmModal,
      closeConfirmModalAsync,
      confirmMessage,
    } = confirmCancelMessageManager();

    const {
      modalInfoNews,
      closeInformationNewsModal,
      news,
      selectedNews,
      createNewNews,
      editNewsSelected,
      createNewsOperation,
      editNewsOperation,
      deleteNewsOperation,
    } = newsUse();

    async function createNewsTransaction(news: {
      title: string;
      description: string;
      image: File;
    }) {
      try {
        await onSpinnerState();
        await createNewsOperation(news.title, news.description, news.image);
        await offSpinnerState();
        showSuccessModal("Noticia creada correctamente");
      } catch (exception) {
        await offSpinnerState();
        throw exception;
      }
    }

    async function editNewsTransaction(news: {
      title: string;
      description: string;
      image: File;
    }) {
      try {
        await onSpinnerState();
        await editNewsOperation(news.title, news.description, news.image);
        await offSpinnerState();
        showSuccessModal("Noticia actualizada correctamente");
      } catch (exception) {
        await offSpinnerState();
        throw exception;
      }
    }

    async function selectDeletedNews(news: News) {
      selectedNews.value = news;
      openConfirmModal("¿Estás seguro de eliminar la noticia?");
    }

    async function deleteNewsTransaction(): Promise<void> {
      try {
        await onSpinnerState();
        await deleteNewsOperation();
        await offSpinnerState();
        showSuccessModal("La noticia ha sido eliminada correctamente");
      } catch (exception) {
        await offSpinnerState();
        throw exception;
      }
    }

    return {
      modalInfoNews,
      createNewsTransaction,
      editNewsTransaction,
      selectDeletedNews,
      deleteNewsTransaction,
      showConfirmModal,
      openConfirmModal,
      closeConfirmModalAsync,
      confirmMessage,
      news,
      closeInformationNewsModal,
      selectedNews,
      createNewNews,
      editNewsSelected,
      createNewsOperation,
      editNewsOperation,
      deleteNewsOperation,
    };
  },
});
</script>

<style scoped lang="scss">
@import "src/ux/news/styles/newsStyle";
</style>
